import {GQLQueryProvider} from "../gqlModel/GQLQueryProvider";
import {QuizModelType} from "./QuizModel";
import {createQuiz, updateQuiz} from "../../graphql/quiz/mutations";
import {QuizQuestionModelType} from "./QuizQuestionModel";
import {deleteQuizQuestionById} from "./requests/deleteQuizQuestion";
import {queryGqlApi} from "../../graphql/queryGqlApi";
import {IDataClientService} from "../../platform/services/data/DataClientService";
import {IAuthService} from "../../platform/services/auth/AuthService";
import type {CreateQuizInput, UpdateQuizInput} from "../../API";

interface QuizGqlProviderDeps {
    dataClientService: IDataClientService;
    authService: IAuthService;
    getCurrentUserId: () => string | null | undefined;
}

export class QuizGqlProvider extends GQLQueryProvider {
    private readonly dataClientService: IDataClientService;
    private readonly authService: IAuthService;
    private readonly getCurrentUserId: () => string | null | undefined;

    constructor({dataClientService, authService, getCurrentUserId}: QuizGqlProviderDeps) {
        super();
        this.dataClientService = dataClientService;
        this.authService = authService;
        this.getCurrentUserId = getCurrentUserId;
    }

    save = async (quiz: QuizModelType): Promise<QuizModelType> => {
        const currentUserId = this.getCurrentUserId() ?? quiz.latestEditor ?? quiz.creator;
        if (!currentUserId) {
            throw new Error('Unable to resolve current user for quiz save operation');
        }

        let savedValue: QuizModelType;

        if (quiz.isNew) {
            const input: CreateQuizInput = {
                id: quiz.id,
                ownerIdAndWsId: quiz.ownerIdAndWsId,
                name: quiz.name,
                description: quiz.description,
                relatedPages: quiz.relatedPages,
                publication_state: quiz.publication_state,
                creator: quiz.creator ?? currentUserId,
                latestEditor: currentUserId,
                requiredPassPercentage: quiz.requiredPassPercentage,
                questionsCount: quiz.safeQuestionsOrder.length,
                questionsOrder: JSON.stringify(quiz.safeQuestionsOrder),
            };

            savedValue = await queryGqlApi(
                createQuiz,
                {input},
                this.dataClientService,
                this.authService,
                'pgServer'
            ) as unknown as QuizModelType;

            for (const quizQuestion of quiz.questions.values()) {
                await (quizQuestion as unknown as QuizQuestionModelType).save();
            }
        } else {
            for (const quizQuestion of quiz.questions.values()) {
                if (quizQuestion.hasChanges) {
                    await (quizQuestion as unknown as QuizQuestionModelType).save();
                }
            }

            for (const quizQuestionId of quiz.deletedQuestionIds.values()) {
                if (quizQuestionId) {
                    await deleteQuizQuestionById(this.dataClientService, this.authService, quizQuestionId);
                }
            }

            const input: UpdateQuizInput = {
                id: quiz.id,
                version: quiz.version,
                ownerIdAndWsId: quiz.ownerIdAndWsId,
                name: quiz.name,
                requiredPassPercentage: quiz.requiredPassPercentage,
                description: quiz.description,
                relatedPages: quiz.relatedPages,
                publication_state: quiz.publication_state,
                latestEditor: currentUserId,
                questionsCount: quiz.safeQuestionsOrder.length,
                questionsOrder: JSON.stringify(quiz.safeQuestionsOrder),
            };

            savedValue = await queryGqlApi(
                updateQuiz,
                {input},
                this.dataClientService,
                this.authService,
                'pgServer'
            ) as unknown as QuizModelType;
        }

        return savedValue;
    };
}
